import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { TokenStorage } from 'modules/core/token-storage';
import { fetchPassedEvents, syncGameInfo } from 'modules/stream/store/game-info/actions';
import { setPassedEventsId as setPassedEventsIdGameInfo } from 'modules/stream/store/game-info/game-info';
import { fetchTemplateWithBoards } from 'modules/template-editor/store/templates/actions';
import { setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AppDispatch } from 'store/store';
import { useQueryParam } from 'use-query-params';
import supabase from '../../../../core/supabase/supabase';
import useTemplates from '../../../../template-editor/ui/hooks/use-templates';
import { getCurrentBoard } from '../../../domain/utils/get-current-board';
import { setBoardLocked, setHideControlPanel, setPassedEventsId as setPassedEventsIdOverlay } from '../../../store/overlay/overlay';
import { syncTime } from '../../../store/time/actions';
import useGameInfo from '../use-game-info';

function useInitDataOverlay() {
  const dispatch = useDispatch<AppDispatch>();
  const { matchId: id } = useParams();

  const matchId = useMemo(() => (id ? parseInt(id) : 0), [id]);
  const encodeToken = useQueryParam('token')[0] as string | undefined;
  const boardId = useQueryParam('boardId')[0] as string | undefined;
  const isLocked = useQueryParam('locked')[0] as string | undefined;
  const isHidePanel = useQueryParam('hidePanel')[0] as string | undefined;
  const { organizer, championship } = useGameInfo();
  const { isLoaded } = useTemplates();

  const isAdmin = useIsAdmin();

  useEffect(() => {
    dispatch(setActiveBoard({ id: boardId ? parseInt(boardId) : undefined }));
    dispatch(setBoardLocked(isLocked === 'true'));
    dispatch(setHideControlPanel(isHidePanel === 'true'));
  }, [boardId, dispatch, isHidePanel, isLocked]);

  useEffect(() => {
    if (!encodeToken) return;
    const token = window.atob(encodeToken);
    const tokenStorage = new TokenStorage();
    tokenStorage.saveToken(token);
  }, [encodeToken]);

  useEffect(() => {
    supabase.overlayLogs.init({ matchId });
    try {
      dispatch(syncGameInfo({ matchId })).then(() => {
        dispatch(syncTime({ matchId }));
        dispatch(fetchPassedEvents({ matchId }));
        supabase.matchChronology.getRow(matchId).then((data) => {
          const passedEvents = data?.chronology.map((event) => event.id) ?? [];
          dispatch(setPassedEventsIdGameInfo(passedEvents));
          dispatch(setPassedEventsIdOverlay(passedEvents));
        });
      });
    } catch (error) {
      if (error instanceof Error) {
        supabase.overlayLogs.createError({
          comment: 'Не удалось сделать первоначальную настройку',
          message: JSON.stringify({ message: error.message, stack: error.stack }),
        });
      }
    }
  }, [dispatch, matchId]);

  useEffect(() => {
    if (!organizer) return;
    if (isLoaded) return;
    supabase.overlayLogs.init({ tournamentId: organizer.id });
  }, [dispatch, isAdmin, isLoaded, organizer]);

  useEffect(() => {
    if (!championship?.overlayId) return;

    dispatch(fetchTemplateWithBoards({ templateId: championship.overlayId }))
      .then(({ payload }) => {
        const { template } = payload as { template: Template; boards: Board[] };
        dispatch(setActiveTemplate({ id: template.id }));
        window?.opener?.postMessage(JSON.stringify({ type: 'popupLoaded' }), '*');
      })
      .catch((data) => {
        supabase.overlayLogs.createError({
          comment: 'Не удалось получить шаблоны пользователя',
          message: data.toString(),
        });
      });

    supabase.overlayLogs.init({
      championshipId: championship.id,
      templateId: championship.overlayId,
    });
  }, [championship?.id, championship?.overlayId, dispatch]);

  useEffect(() => {
    if (!isLoaded) return;
    if (boardId) return;
    getCurrentBoard(matchId).then((currentBoard) => {
      dispatch(setActiveBoard({ id: currentBoard }));
    });
  }, [dispatch, matchId, isLoaded, boardId]);
}

export default useInitDataOverlay;
